import { ICompanyDataModel } from '../../data-models/company.data-model';
import {
  IFundMetricsRequestDataModel,
  IFundMetricsResponseDataModel,
} from '../../data-models/fund-metrics.data-model';
import {
  ICompanyMetricsDataModel,
  IMetricsDataModel,
  IMetricsResponseDataModel,
} from '../../data-models/metrics.data-model';
import { IInvestmentDataModel } from '../../data-models/investment.data-model';
import { MaggieHttp } from '../MaggieHttp';
import { Person } from '../../schemas/Person.schema';
import { SecurityReport } from '../../schemas/SecurityReport.schema';
import { FundContributionsResponse } from '../../schemas/FundContributions.schema';
import { InvestorCashFlowsResponse, InvestorCashFlowType } from '../../schemas/InvestorCashFlows.schema';
import { OwnershipTimelineEvent, OwnershipTimelineRequest } from '../../schemas/OwnershipTimeline.schema';
import { dateToPlainDateString } from '../../util/date-utilts';
import { IFinanceDashboardResponseDataModel } from '../../data-models/dashboard-transaction.data-model';
import { FetchDashboardDataParams } from './MaggieTransactionQueries';

export async function getCompanyMetrics(
  companyId?: number | null,
  currency?: string
): Promise<ICompanyMetricsDataModel> {
  const res = await MaggieHttp.maggie.get<IMetricsResponseDataModel>(`/metrics`, {
    params: {
      companyIds: [companyId],
      currencyCode: currency,
    },
  });
  const metrics = { ...getMetricsWithCompany(res.data.metrics[0], res.data) };
  return metrics ? { ...metrics } : ({} as ICompanyMetricsDataModel);
}

export async function fetchCompanyMetrics(
  companyId: number,
  asOfDate?: string,
  currency?: string,
  calculateFundTypeData = false
): Promise<IMetricsResponseDataModel> {
  const res = await MaggieHttp.maggie.get<IMetricsResponseDataModel>(`/metrics`, {
    params: {
      companyIds: [companyId],
      asOfDate,
      currencyCode: currency,
      calculateFundTypeData,
    },
  });
  return res.data;
}

/** 'fund': one row per fund in each round; a round no selected fund invested in keeps one round row. */
export type RoundTrackerGrain = 'round' | 'fund';

export async function fetchRoundTracker(
  asOfDate?: string,
  currency?: string,
  companyIds?: number[],
  fundIds?: number[],
  includeNonParticipatingRounds?: boolean,
  grain?: RoundTrackerGrain
): Promise<IInvestmentDataModel[]> {
  const response = await MaggieHttp.maggie.post<
    {
      asOfDate?: string;
      currencyCode?: string;
      companyIds?: number[];
      fundIds?: number[];
      includeNonParticipatingRounds?: boolean;
      grain?: RoundTrackerGrain;
    },
    IInvestmentDataModel[]
  >('/metrics/roundtracker/query', {
    asOfDate,
    currencyCode: currency,
    companyIds,
    fundIds,
    includeNonParticipatingRounds,
    grain,
  });

  return response.data;
}

export async function fetchOwnershipTimeline(
  companyId: number,
  asOfDate?: string,
  currency?: string
): Promise<OwnershipTimelineEvent[]> {
  const response = await MaggieHttp.maggie.post<OwnershipTimelineRequest, OwnershipTimelineEvent[]>(
    '/metrics/ownership-timeline/query',
    {
      companyId,
      asOfDate,
      currencyCode: currency,
    }
  );

  return response.data;
}

export function getPlainDateString(dateArg?: Date | null): string {
  const date = !dateArg ? new Date() : dateArg;
  return dateToPlainDateString(date);
}
export function getMetricsWithCompany(metrics: IMetricsDataModel, data: IMetricsResponseDataModel) {
  const company = data.included?.find((comp) => comp.id === metrics.companyId);
  return { ...metrics, company: company as ICompanyDataModel, companyId: company!.id! };
}

export async function fetchMetrics(
  asOfDate: string = getPlainDateString(),
  currency?: string,
  calculateFundTypeData = false
) {
  const { data } = await MaggieHttp.maggie.get<IMetricsResponseDataModel>(`/metrics`, {
    // Omitted entirely (not sent as `false`) so the default request stays byte-identical to
    // before this param existed — the api takes a pre-compressed Brotli fast path only when
    // calculateFundTypeData is absent.
    params: {
      asOfDate,
      currencyCode: currency,
      ...(calculateFundTypeData ? { calculateFundTypeData: true } : {}),
    },
  });

  return data;
}
export async function getFundMetrics(
  asOfDate: string = getPlainDateString(),
  fundIds?: number[],
  currencyCode?: string
) {
  const { data } = await MaggieHttp.maggie.post<IFundMetricsRequestDataModel, IFundMetricsResponseDataModel>(
    `metrics/fundmetrics`,
    { asOfDate, fundIds, currencyCode }
  );

  return data as IFundMetricsResponseDataModel;
}

export interface IMetricsCurrencies {
  /** Currencies metrics are computed in: every fund's reporting currency plus USD. */
  computed: string[];
  /** fundId → that fund's reporting currency. */
  fundDefaults: Record<number, string>;
}

export async function getMetricsCurrencies(): Promise<IMetricsCurrencies> {
  const { data } = await MaggieHttp.maggie.get<IMetricsCurrencies>('/metrics/currencies');
  return data;
}

export async function getFundContributions(asOfDate: string = getPlainDateString(), fundIds?: number[]) {
  const { data } = await MaggieHttp.maggie.get<FundContributionsResponse[]>(
    `metrics/fundmetrics/contributions`,
    {
      params: { asOfDate, fundIds },
    }
  );
  return data;
}

export interface IGetFundInvestorCashFlowsParams {
  fundId: number;
  investorType?: InvestorCashFlowType;
  from?: string; // 'YYYY-MM-DD'
  to?: string; // 'YYYY-MM-DD'
  includeFeesAndExpenses?: boolean;
}
export async function getFundInvestorCashFlows({
  fundId,
  investorType,
  from,
  to,
  includeFeesAndExpenses,
}: IGetFundInvestorCashFlowsParams) {
  const { data } = await MaggieHttp.maggie.get<InvestorCashFlowsResponse>(
    `metrics/fundmetrics/investor-cash-flows`,
    {
      params: {
        fundId,
        investorType,
        from,
        to,
        // The API only enables it on the literal 'true', so a false flag is left off the request.
        includeFeesAndExpenses: includeFeesAndExpenses ? 'true' : undefined,
      },
    }
  );
  return data;
}

export async function getPeople() {
  const { data } = await MaggieHttp.maggie.get<Person[]>('/metrics/people-reporting');
  return data;
}

interface IGetSecurityReportParams {
  companyIds?: number[];
  date?: string;
  currency?: string;
}
export async function getSecurityReport(params: IGetSecurityReportParams = {}): Promise<SecurityReport[]> {
  const { data } = await MaggieHttp.maggie.get<SecurityReport[]>('/metrics/securityReport', {
    params,
  });
  return data;
}
export async function fetchDashboardData({
  companyIds,
  fundIds,
  asOfDate,
}: FetchDashboardDataParams): Promise<IFinanceDashboardResponseDataModel> {
  const response = await MaggieHttp.maggie.post<FetchDashboardDataParams, IFinanceDashboardResponseDataModel>(
    '/metrics/dashboard',
    {
      companyIds,
      fundIds,
      asOfDate,
    }
  );
  return response.data;
}
